import React, { useContext } from "react";
import propTypes from "prop-types";
import classnames from "classnames";
import { FormContext } from "./Form";

import "./Form.scss";
import { createDataAttributes } from "../../utilities";

const formFieldTypes = [
  "BinaryToggle",
  "Checkbox",
  "DatePicker",
  "DateRange",
  "Dropdown",
  "Input",
  "InputApiCall",
  "InputEmail",
  "InputSearch",
  "RadioButton",
  "RadioButtonContainer",
  "RadioList",
  "RadioPanel",
  "RadioPanelContainer",
  "SecretField",
  "Select",
  "Switch",
  "TextArea"
];

export const FormGroup = ({
  children,
  className,
  wrap,
  align,
  noOuterPadding,
  hideErrors,
  groupErrors,
  dataAttrib,
  ...rest
}) => {
  const { errors } = useContext(FormContext);

  const classes = classnames("uikit__form-group", className, {
    wrap,
    "uikit__form-group__no-outer-padding": noOuterPadding,
    "uikit__form-group__hide-errors": hideErrors || groupErrors
  });
  const itemsClass = classnames("uikit__form-group__items", {
    "uikit__form-group__align-left": align === "left",
    "uikit__form-group__align-center": align === "center",
    "uikit__form-group__align-justify": align === "justify",
    "uikit__form-group__align-right": align === "right"
  });

  // Modified addErrors function to handle wrapped components
  const addErrors = child => {
    if (
      child?.props?.hasOwnProperty("id") &&
      child?.props?.hasOwnProperty("value")
    ) {
      const id = child.props.id;
      const fieldErrors = errors?.[id] ? errors[id] : [];

      return React.cloneElement(child, {
        ...child.props,
        fieldErrors,
        hideErrors: child?.props?.hideErrors || hideErrors || groupErrors
      });
    }
    return child;
  };

  const fieldsInGroup = [];
  const findFieldsInGroup = child => {
    if (formFieldTypes.includes(child?.props?.componentname)) {
      fieldsInGroup.push(child.props.id);
    }
  };

  recursiveMap(children, findFieldsInGroup);

  return (
    <>
      <div className={classes} {...createDataAttributes(dataAttrib)} {...rest}>
        <div className={itemsClass}>
          {children && recursiveMap(children, addErrors)}
        </div>
      </div>
      {groupErrors && !!fieldsInGroup.length && (
        <FieldErrors fields={fieldsInGroup} />
      )}
    </>
  );
};

const WrappedComponent = ({ component, props, fn }) => {
  const rendered = component(props);
  if (fn && React.isValidElement(rendered)) {
    return fn(rendered);
  }
  return rendered;
};

// a function to loop through nested children
const recursiveMap = (children, fn) => {
  return React.Children.map(children, child => {
    if (!React.isValidElement(child)) {
      return child;
    }

    const compName = child.props.componentname;
    if (compName && formFieldTypes.includes(compName)) {
      return fn(child);
    }

    // If it's a function component, try to render it and process its output
    if (typeof child.type === "function") {
      // Get the rendered output
      return React.createElement(WrappedComponent, {
        component: child.type,
        props: child.props,
        fn
      });
    }

    // Handle nested children
    if (child.props.children) {
      const nestedChildren = React.Children.toArray(child.props.children);
      return React.cloneElement(child, {
        ...child.props,
        children: recursiveMap(nestedChildren, fn)
      });
    }

    return fn(child);
  });
};

FormGroup.propTypes = {
  wrap: propTypes.bool,
  className: propTypes.string,
  children: propTypes.any,
  noOuterPadding: propTypes.bool,
  hideErrors: propTypes.bool,
  groupErrors: propTypes.bool,
  align: propTypes.oneOf(["left", "center", "justify", "right"]),
  dataAttrib: propTypes.arrayOf(Object)
};

FormGroup.defaultProps = {
  wrap: true,
  hideErrors: false
};

// use FieldErrors to display errors in a group
export const FieldErrors = ({ fields, className, ...rest }) => {
  const { errors } = useContext(FormContext);

  if (!fields || !Array.isArray(fields)) return null;

  const errs = [];
  fields.forEach(f => {
    if (errors[f]) errs.push(...errors[f]);
  });

  if (!errs.length) return null;

  const classes = classnames("uikit__form__field-errors", className, {
    "uikit__error-group__multiple": errs.length > 1
  });

  return (
    <div className={classes} {...rest}>
      <ul>
        {errs.map(e => {
          return <li key={e}>{e}</li>;
        })}
      </ul>
    </div>
  );
};

FieldErrors.propTypes = {
  fields: propTypes.array,
  className: propTypes.string,
  children: propTypes.any
};

FieldErrors.defaultProps = {};
